import { useEffect } from 'react';
import { useActionBar } from 'common/actionBar/hooks/useActionBar';
import { Stack, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { getCandidateProfileSelectionActionBarItems } from 'modules/candidate-profile/utils/getCandidateProfileSelectionActionBarItems';
import { CandidateProfileListProps } from 'modules/candidate-profile/types/CandidateProfileListProps';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

type UseCandidateProfileSelectionBarProps = {
  candidateProfileListProps: CandidateProfileListProps;
  selectedCandidateIds: string[];
  onDeselectAll: () => void;
  onInviteToApply: () => void;
  onAddToTalentList: () => Promise<void>;
  onAddToWorkflow: () => void;
  onRemoveFromTalentList: () => void;
  onAddToJob: () => void;
  onAddToJobAction: () => void;
  isAtsConnected: boolean;
  hasAddToJobAction?: boolean;
  isAddToJobActionOverLimit?: boolean;
  isFromWorkflowBuilder?: boolean;
  isWorkflowBuilderEnabled?: boolean;
};

export const useCandidateProfileSelectionBar = ({
  candidateProfileListProps,
  selectedCandidateIds,
  onDeselectAll,
  onInviteToApply,
  onAddToTalentList,
  onAddToWorkflow,
  onRemoveFromTalentList,
  onAddToJob,
  onAddToJobAction,
  isAtsConnected,
  hasAddToJobAction,
  isAddToJobActionOverLimit,
  isFromWorkflowBuilder,
  isWorkflowBuilderEnabled
}: UseCandidateProfileSelectionBarProps) => {
  const { openActionBar, closeActionBar } = useActionBar();
  const isInviteToApplyHidden = Boolean(useFlag(Flags.HIDE_INVITE_TO_APPLY));

  useEffect(() => {
    if (selectedCandidateIds.length === 0) {
      closeActionBar();
      return;
    }

    const items = getCandidateProfileSelectionActionBarItems({
      candidateProfileListProps,
      onInviteToApply,
      onAddToTalentList,
      onAddToWorkflow,
      onRemoveFromTalentList,
      onAddToJob,
      onAddToJobAction,
      isAtsConnected,
      hasAddToJobAction,
      isAddToJobActionOverLimit,
      isFromWorkflowBuilder,
      isWorkflowBuilderEnabled,
      isInviteToApplyHidden
    });

    openActionBar({
      align: 'left',
      children: (
        <Stack direction="row" gap="20px" alignItems="center">
          {items}
          <Button variant="tertiary" onClick={onDeselectAll}>
            Deselect all
          </Button>
          <Typography>
            {selectedCandidateIds.length} candidate
            {selectedCandidateIds.length !== 1 ? 's' : ''} selected
          </Typography>
        </Stack>
      )
    });

    return () => {
      closeActionBar();
    };
  }, [
    selectedCandidateIds,
    onDeselectAll,
    onInviteToApply,
    onAddToTalentList,
    onAddToWorkflow,
    onRemoveFromTalentList,
    onAddToJob,
    onAddToJobAction,
    isAtsConnected,
    hasAddToJobAction,
    isAddToJobActionOverLimit,
    isInviteToApplyHidden,
    openActionBar,
    closeActionBar
  ]);
};
